<template>
  <div class="flex gap-16">
    <div v-if="image" class="h-64 w-64">
      <RevIllustration alt="product" :height="64" :src="image" :width="64" />
    </div>
    <div class="ml-4 flex grow flex-col justify-center">
      <div v-if="id" class="flex items-center gap-x-1 mb-6">
        <span class="caption">{{ i18n(translations.tradeInId) }}:</span>
        <RevTooltip position="top">
          <template #trigger="{ show, hide }">
            <button
              class="flex gap-x-2 items-center px-2 caption"
              @click="handleClick(show, hide)"
              @focusout="hide"
              @mouseleave="hide"
            >
              <span data-test="product-id">{{ id }} </span>
              <IconCopy data-test="copy-icon" size="small" />
            </button>
          </template>
          {{ i18n(translations.orderIdCopied) }}
        </RevTooltip>
      </div>
      <RevTag
        v-if="tag"
        class="mb-3 self-start"
        :label="tag"
        variant="alternate"
      />
      <p class="body-2-bold">{{ title }}</p>
      <p class="mt-1">
        <span
          v-if="counterOfferPrice"
          class="text-static-default-low body-2-bold"
          data-test="counter-offer-price"
        >
          {{ counterOfferPrice }}
        </span>
        <span :class="priceClasses">{{ price }}</span>
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { tw } from '@backmarket/utils/string/tw'
import { RevIllustration } from '@ds/components/Illustration'
import { RevTag } from '@ds/components/Tag'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconCopy } from '@ds/icons/IconCopy'

import translations from './ProductInfo.translations'

const i18n = useI18n()

interface ProductInfoProps {
  id?: string
  image?: string
  title?: string
  price?: string
  tag?: string
  counterOfferPrice?: string
}

const props = withDefaults(defineProps<ProductInfoProps>(), {
  id: '',
  image: '',
  title: '',
  price: '',
  tag: '',
  counterOfferPrice: '',
})

const priceClasses = computed(() => {
  return props.counterOfferPrice
    ? tw`text-static-default-low caption-striked line-through ml-6`
    : tw`body-2-bold text-primary-light`
})

const handleClick = async (show: () => void, hide: () => void) => {
  try {
    await navigator.clipboard.writeText(props.id)
    show()

    setTimeout(() => {
      hide()
    }, 2000)
  } catch (e) {
    // Do nothing
  }
}
</script>
